import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildService, buildBreadcrumb } from "@/lib/seo";
import { Check, Shield, Clock, FileText } from "lucide-react";

const REASONS = [
  { icon: Clock, title: "Time-sensitive situations", body: "Foreclosure dates, probate deadlines, tax sale dates — we route you fast." },
  { icon: Shield, title: "Verified buyers only", body: "Investors upload proof of funds. Realtors, attorneys, and contractors are reviewed." },
  { icon: FileText, title: "Clear paperwork", body: "Every offer includes amount, closing days, conditions, and your estimated net." },
];

export default function ForSellers() {
  return (
    <div>
      <Seo
        title="For Sellers — Get Multiple Options for Your Distressed Property"
        description="Behind on mortgage, in foreclosure, probate, divorce, or just need a fast cash sale? ADN matches you with verified investors, Realtors, attorneys, and contractors. Free, no obligation."
        path="/for-sellers"
        jsonLd={[
          buildService(
            "Distressed property seller matching",
            "Match distressed homeowners with verified cash buyers, Realtors, attorneys, lenders, and contractors. Free for sellers.",
          ),
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "For Sellers", url: "https://adn-marketplace.com/for-sellers" },
          ]),
        ]}
      />
      <Navbar />
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <p className="eyebrow text-[var(--adn-gold)]">For sellers & homeowners</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light mt-3 max-w-3xl">You have more options than you think.</h1>
          <p className="mt-6 text-white/75 max-w-2xl text-lg leading-relaxed">
            Whether you're behind on payments, going through probate, dealing with repairs, or just need a fast exit — ADN matches you with the right people for your situation.
          </p>
          <div className="mt-10 flex gap-3">
            <Link to="/submit-property" data-testid="fs-submit" className="btn-gold px-7 py-4 rounded text-sm">Submit My Property</Link>
            <Link to="/how-it-works" className="btn-outline-light px-7 py-4 rounded text-sm">How It Works</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-6">
        {REASONS.map((r, i) => (
          <div key={i} className="bg-white border border-[var(--adn-line)] p-7">
            <r.icon size={22} className="text-[var(--adn-gold)]" />
            <h3 className="font-display text-xl font-semibold mt-4">{r.title}</h3>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">{r.body}</p>
          </div>
        ))}
      </section>

      <section className="bg-white border-y border-[var(--adn-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow">Situations we handle</p>
            <h2 className="font-display text-4xl font-medium mt-3">Every distressed scenario.</h2>
          </div>
          <ul className="grid grid-cols-2 gap-3">
            {["Behind on mortgage","Foreclosure","Probate","Divorce","Tax delinquent","Vacant property","Code violations","Needs repairs","Inherited","Need fast cash","Want highest buyer","Avoid foreclosure"].map((s) => (
              <li key={s} className="flex items-center gap-2 text-[15px]"><Check size={16} className="text-[var(--adn-emerald)]" />{s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20">
        <p className="eyebrow">Frequently asked</p>
        <h2 className="font-display text-4xl font-medium mt-3 mb-10">Common questions from sellers.</h2>
        <div className="space-y-7">
          {[
            { q: "Will using ADN hurt my credit or affect my loan?", a: "No. Submitting your property to ADN does not run a credit check, contact your lender, or change your loan in any way. ADN is a marketplace that organizes options for you. If you ultimately accept an offer that involves your existing loan (for example a subject-to or loan modification), the provider will explain those specifics before you commit to anything." },
            { q: "Do I have to accept any offer I receive?", a: "Absolutely not. Every offer is yours to compare, negotiate, decline, or ignore. There is no obligation to accept anything. The whole point of ADN is to give you more than one option so you can choose what is best for you." },
            { q: "What if my property is in really bad shape?", a: "That is exactly what ADN is built for. Cash investors specifically look for properties that need work. Contractors on the platform can also send you repair estimates if you want to compare fixing it up versus selling as-is." },
            { q: "Can I sell if I am behind on payments or in foreclosure?", a: "Yes. Many of the sellers on ADN are in pre-foreclosure or already in the foreclosure process. Speed matters: the sooner you submit, the more time providers have to send you cash offers, loan modification referrals, or short-sale options." },
            { q: "How private is my information?", a: "Your exact address is hidden from providers until you accept an offer. Providers see city, state, situation tags, and the AI summary of your property. Your contact information is only shared with providers you choose to engage." },
          ].map((f, i) => (
            <details key={i} className="border-b border-[var(--adn-line)] pb-5 group">
              <summary className="font-display text-lg font-semibold cursor-pointer list-none flex items-start justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-[var(--adn-gold)] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[var(--adn-text-soft)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-12 bg-[var(--adn-navy)] text-white p-8 grain">
          <p className="eyebrow text-[var(--adn-gold)]">Ready when you are</p>
          <h3 className="font-display text-2xl font-semibold mt-2">Submit once. Compare everything. Decide on your timeline.</h3>
          <p className="text-white/70 mt-3 max-w-2xl">Sellers always submit free. There is no obligation. Most homeowners receive multiple offers within 24 to 72 hours of submission, and cash sales can close in as little as 7 to 21 days when needed.</p>
          <Link to="/submit-property" className="btn-gold mt-6 inline-flex px-6 py-3 rounded text-sm">Submit My Property</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
